import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import {
  Container,
  Typography,
  Paper,
  Box,
  Grid,
  Card,
  CardContent,
  Button,
  AppBar,
  Toolbar,
  IconButton,
  Tabs,
  Tab,
  Chip,
  CircularProgress,
  Alert,
  Avatar,
  Divider
} from '@mui/material';
import {
  AccountCircle,
  ExitToApp,
  Dashboard as DashboardIcon,
  CalendarToday,
  Chat as ChatIcon,
  Person,
  AccessTime,
  CheckCircle,
  HourglassEmpty,
  AttachMoney,
  Message,
  Star,
  AccountBalance
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import { performLogout } from '../../utils/logout';
import {
  fetchAstrologerSummary,
  fetchAstrologerUpcomingConsultations,
  fetchAstrologerRecentMessages,
  fetchAstrologerEarnings
} from '../../store/slices/astrologerDashboardSlice';
import { fetchPayoutEligibility } from '../../store/slices/earningsSlice';
import { useChat } from '../../contexts/ChatContext';
import { useCall } from '../../contexts/CallContext';
import api from '../../services/api';
import AstrologerStatusToggle from '../consultation/AstrologerStatusToggle';
import IncomingCallModal from '../consultation/IncomingCallModal';
import NotificationBell from '../notifications/NotificationBell';

const StatCard = ({ title, value, icon: Icon, color = 'primary', subtitle }) => (
  <Card sx={{ height: '100%' }}>
    <CardContent>
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
        <Box>
          <Typography variant="body2" color="text.secondary" gutterBottom>
            {title}
          </Typography>
          <Typography variant="h4" color={`${color}.main`}>
            {value}
          </Typography>
          {subtitle && (
            <Typography variant="caption" color="text.secondary">
              {subtitle}
            </Typography>
          )}
        </Box>
        <Box
          sx={{
            width: 56,
            height: 56,
            borderRadius: '50%',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            bgcolor: `${color}.light`,
            opacity: 0.8
          }}
        >
          <Icon sx={{ fontSize: 28, color: `${color}.main` }} />
        </Box>
      </Box>
    </CardContent>
  </Card>
);

const ConsultationItem = ({ consultation, onJoinChat }) => {
  const { user, slot, status, can_join_chat: canJoinChat, consultation_id: consultationId } = consultation;

  return (
    <>
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', py: 1.5 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
          <Avatar src={user?.profile_image} alt={user?.name} sx={{ width: 48, height: 48 }}>
            {user?.name?.[0] || 'U'}
          </Avatar>
          <Box>
            <Typography variant="subtitle1" fontWeight="medium">
              {user?.name || 'User'}
            </Typography>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mt: 0.5 }}>
              <CalendarToday sx={{ fontSize: 14, color: 'text.secondary' }} />
              <Typography variant="caption" color="text.secondary">
                {slot?.date}
              </Typography>
              <AccessTime sx={{ fontSize: 14, color: 'text.secondary', ml: 1 }} />
              <Typography variant="caption" color="text.secondary">
                {slot?.start_time} - {slot?.end_time}
              </Typography>
            </Box>
          </Box>
        </Box>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <Chip
            label={status?.charAt(0).toUpperCase() + status?.slice(1)}
            size="small"
            color={status === 'confirmed' ? 'success' : 'default'}
          />
          {canJoinChat ? (
            <Button
              variant="contained"
              size="small"
              startIcon={<ChatIcon />}
              onClick={() => onJoinChat(consultationId)}
            >
              Join Chat
            </Button>
          ) : (
            <Chip label="Starts soon" size="small" color="info" variant="outlined" />
          )}
        </Box>
      </Box>
      <Divider />
    </>
  );
};

const MessageItem = ({ message }) => {
  const timeAgo = (() => {
    try {
      const date = new Date(message.timestamp);
      const now = new Date();
      const diffMs = now - date;
      const diffMins = Math.floor(diffMs / 60000);
      if (diffMins < 60) return `${diffMins}m ago`;
      const diffHours = Math.floor(diffMins / 60);
      if (diffHours < 24) return `${diffHours}h ago`;
      return `${Math.floor(diffHours / 24)}d ago`;
    } catch {
      return '';
    }
  })();

  return (
    <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 2, py: 1.5, borderBottom: '1px solid', borderColor: 'divider' }}>
      <Avatar sx={{ width: 36, height: 36, bgcolor: message.is_read ? 'grey.300' : 'primary.main' }}>
        <Message sx={{ fontSize: 18 }} />
      </Avatar>
      <Box sx={{ flex: 1 }}>
        <Typography variant="body2" fontWeight={message.is_read ? 'normal' : 'medium'}>
          {message.user_name}: {message.content?.substring(0, 50)}{message.content?.length > 50 ? '...' : ''}
        </Typography>
        <Typography variant="caption" color="text.secondary">
          {timeAgo}
        </Typography>
      </Box>
      {!message.is_read && (
        <Chip label="New" size="small" color="primary" />
      )}
    </Box>
  );
};

const AstrologerDashboard = () => {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const { startCall } = useCall();
  const [activeTab, setActiveTab] = useState(0);
  const [incomingCall, setIncomingCall] = useState(null); // { consultationId, type, callerName, ratePerMin }

  const { user } = useSelector((state) => state.auth);
  const { socket, connectGlobal } = useChat();
  const {
    summary,
    upcomingConsultations,
    upcomingTotal,
    recentMessages,
    earnings,
    loading,
    error
  } = useSelector((state) => state.astrologerDashboard);
  const { eligibility, loading: earningsLoading } = useSelector((state) => state.earnings);

  useEffect(() => {
    // Auth check is handled by AstrologerRoute wrapper — no need to check here.
    // Tokens are in httpOnly cookies (not localStorage).

    // Stagger dashboard API calls to avoid hitting rate limits on simultaneous burst
    dispatch(fetchAstrologerSummary());
    const t1 = setTimeout(() => dispatch(fetchAstrologerUpcomingConsultations({ limit: 5 })), 200);
    const t2 = setTimeout(() => dispatch(fetchAstrologerRecentMessages({ limit: 10 })), 400);
    const t3 = setTimeout(() => dispatch(fetchAstrologerEarnings()), 600);
    const t4 = setTimeout(() => dispatch(fetchPayoutEligibility()), 800);
    return () => { clearTimeout(t1); clearTimeout(t2); clearTimeout(t3); clearTimeout(t4); };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dispatch]);

  // Request browser notification permission (for incoming calls when tab is hidden)
  useEffect(() => {
    if ('Notification' in window && Notification.permission === 'default') {
      Notification.requestPermission();
    }
  }, []);

  // Connect socket globally for incoming call notifications
  useEffect(() => {
    const stored = user || JSON.parse(localStorage.getItem('user') || '{}');
    if (stored?.id) {
      connectGlobal(stored.id, 'astrologer');
    }
  }, [user, connectGlobal]);

  // Listen for incoming calls from users (socket-based, fast path)
  useEffect(() => {
    if (!socket) return;

    const handleIncomingCall = (data) => {
      setIncomingCall({
        consultationId: data.consultationId || data.consultation_id,
        type: data.type || 'chat',
        userName: data.userName || data.user_name || 'User',
        ratePerMin: data.ratePerMin || data.rate_per_min || 10,
        chatRoomId: data.chatRoomId || data.chat_room_id,
      });
    };

    const handleCallCancelled = () => setIncomingCall(null);

    socket.on('incoming-call', handleIncomingCall);
    socket.on('call-cancelled', handleCallCancelled);
    return () => {
      socket.off('incoming-call', handleIncomingCall);
      socket.off('call-cancelled', handleCallCancelled);
    };
  }, [socket]);

  // Polling fallback: check for incoming calls every 3s (handles missed socket events)
  useEffect(() => {
    const poll = async () => {
      try {
        const resp = await api.get('/astrologer/incoming-call');
        const incoming = resp.data?.incoming;
        if (incoming) {
          setIncomingCall(prev =>
            prev?.consultationId === incoming.consultationId ? prev : {
              consultationId: incoming.consultationId,
              type: incoming.type || 'chat',
              userName: incoming.userName || 'User',
              ratePerMin: incoming.ratePerMin || 10,
              chatRoomId: incoming.chatRoomId,
            }
          );
        } else {
          setIncomingCall(prev => prev ? null : prev); // Only update if currently has a call
        }
      } catch (_) {}
    };

    const id = setInterval(poll, 5000);
    return () => clearInterval(id);
  }, []);

  const handleLogout = async () => {
    await performLogout(navigate);
  };

  // Called by IncomingCallModal after /accept succeeds
  const handleCallAccepted = (chatRoomId, consultationId, type, ratePerMin) => {
    setIncomingCall(null);
    // Start call globally — PiP appears via GlobalCallPiP in App.js
    // Astrologer stays on dashboard (no navigation)
    startCall(consultationId, type, {
      chatRoomId: chatRoomId || `instant_${consultationId}`,
      displayName: user?.name || 'Astrologer',
      ratePerMin: ratePerMin || 0,
    });
  };

  const handleRejectCall = () => {
    setIncomingCall(null);
  };

  const handleJoinChat = (consultationId) => {
    if (consultationId) {
      navigate(`/astrologer/chat/${consultationId}`);
    } else {
      alert('The consultation session has not started yet.');
    }
  };

  const handleTabChange = (event, newValue) => {
    setActiveTab(newValue);
  };

  const userData = user || JSON.parse(localStorage.getItem('user') || '{}');

  return (
    <>
      <AppBar position="static">
        <Toolbar>
          <Typography variant="h6" component="div" sx={{ flexGrow: 1 }}>
            Astro Sastra - Astrologer Dashboard
          </Typography>
          <Button color="inherit" onClick={() => navigate('/astrologer/earnings')} sx={{ mr: 1 }}>
            Earnings
          </Button>
          <AstrologerStatusToggle sx={{ mr: 2 }} />
          <NotificationBell />
          <IconButton color="inherit">
            <AccountCircle />
          </IconButton>
          <Typography variant="body1" sx={{ mr: 2 }}>
            {userData.firstName || userData.name} {userData.lastName || ''}
          </Typography>
          <IconButton color="inherit" onClick={handleLogout}>
            <ExitToApp />
          </IconButton>
        </Toolbar>
      </AppBar>

      <IncomingCallModal
        open={!!incomingCall}
        call={incomingCall}
        autoRejectAfter={30000}
        onAccepted={handleCallAccepted}
        onRejected={handleRejectCall}
      />

      <Container maxWidth="lg" sx={{ mt: 4, mb: 4 }}>
        <Paper sx={{ mb: 3 }}>
          <Tabs
            value={activeTab}
            onChange={handleTabChange}
            indicatorColor="primary"
            textColor="primary"
            variant="fullWidth"
          >
            <Tab icon={<DashboardIcon />} label="Overview" />
            <Tab icon={<CalendarToday />} label="Set Availability" />
            <Tab icon={<ChatIcon />} label="Consultations" />
          </Tabs>
        </Paper>

        {activeTab === 0 ? (
          <>
            <Typography variant="h4" component="h1" gutterBottom>
              Welcome back, {userData.firstName || userData.name || 'Astrologer'}!
            </Typography>

            {/* Stats Cards */}
            {loading.summary ? (
              <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
                <CircularProgress />
              </Box>
            ) : error.summary ? (
              <Alert severity="error" sx={{ mb: 3 }}>{error.summary}</Alert>
            ) : (
              <Grid container spacing={3} sx={{ mb: 4 }}>
                <Grid item xs={12} sm={6} md={3}>
                  <StatCard
                    title="Today's Earnings"
                    value={`₹${summary?.today_earnings || 0}`}
                    icon={AttachMoney}
                    color="success"
                  />
                </Grid>
                <Grid item xs={12} sm={6} md={3}>
                  <StatCard
                    title="Month's Earnings"
                    value={`₹${summary?.month_earnings || 0}`}
                    icon={AttachMoney}
                    color="primary"
                  />
                </Grid>
                <Grid item xs={12} sm={6} md={3}>
                  <StatCard
                    title="Total Consultations"
                    value={summary?.total_consultations || 0}
                    icon={ChatIcon}
                    color="info"
                    subtitle={`${summary?.active_consultations || 0} active`}
                  />
                </Grid>
                <Grid item xs={12} sm={6} md={3}>
                  <StatCard
                    title="Rating"
                    value={summary?.rating?.toFixed(1) || '0.0'}
                    icon={Star}
                    color="warning"
                    subtitle={`${summary?.pending_messages || 0} unread messages`}
                  />
                </Grid>
              </Grid>
            )}

            {/* Payout Method Banner */}
            {!earningsLoading.eligibility && eligibility?.checks?.payout_method_added === false && (
              <Alert
                severity="warning"
                sx={{ mb: 2 }}
                action={
                  <Button
                    color="inherit"
                    size="small"
                    onClick={() => navigate('/astrologer/earnings?tab=3')}
                  >
                    Set Up Now
                  </Button>
                }
              >
                Set up a payout method (bank account or UPI) to withdraw your earnings.
              </Alert>
            )}

            <Grid container spacing={3}>
              {/* Upcoming Consultations */}
              <Grid item xs={12} md={8}>
                <Card>
                  <CardContent>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                      <Typography variant="h6">Upcoming Consultations</Typography>
                      {upcomingTotal > 5 && (
                        <Button size="small" onClick={() => setActiveTab(2)}>
                          View All ({upcomingTotal})
                        </Button>
                      )}
                    </Box>

                    {loading.upcoming ? (
                      <Box sx={{ display: 'flex', justifyContent: 'center', py: 3 }}>
                        <CircularProgress />
                      </Box>
                    ) : error.upcoming ? (
                      <Alert severity="error">{error.upcoming}</Alert>
                    ) : upcomingConsultations.length === 0 ? (
                      <Box sx={{ textAlign: 'center', py: 3 }}>
                        <Typography variant="body1" color="text.secondary" gutterBottom>
                          No upcoming consultations
                        </Typography>
                        <Button
                          variant="outlined"
                          size="small"
                          onClick={() => setActiveTab(1)}
                          startIcon={<CalendarToday />}
                        >
                          Set Availability
                        </Button>
                      </Box>
                    ) : (
                      <Box>
                        {upcomingConsultations.map((consultation) => (
                          <ConsultationItem
                            key={consultation.booking_id}
                            consultation={consultation}
                            onJoinChat={handleJoinChat}
                          />
                        ))}
                      </Box>
                    )}
                  </CardContent>
                </Card>
              </Grid>

              {/* Recent Messages */}
              <Grid item xs={12} md={4}>
                <Card sx={{ height: '100%' }}>
                  <CardContent>
                    <Typography variant="h6" gutterBottom>
                      Recent Messages
                    </Typography>

                    {loading.messages ? (
                      <Box sx={{ display: 'flex', justifyContent: 'center', py: 3 }}>
                        <CircularProgress size={24} />
                      </Box>
                    ) : error.messages ? (
                      <Alert severity="error">{error.messages}</Alert>
                    ) : recentMessages.length === 0 ? (
                      <Typography variant="body2" color="text.secondary" sx={{ py: 2, textAlign: 'center' }}>
                        No recent messages
                      </Typography>
                    ) : (
                      <Box>
                        {recentMessages.slice(0, 5).map((message) => (
                          <MessageItem key={message.id} message={message} />
                        ))}
                      </Box>
                    )}
                  </CardContent>
                </Card>
              </Grid>

              {/* Quick Actions */}
              <Grid item xs={12}>
                <Paper sx={{ p: 2 }}>
                  <Typography variant="h6" gutterBottom>
                    Quick Actions
                  </Typography>
                  <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
                    <Button
                      variant="contained"
                      startIcon={<CalendarToday />}
                      onClick={() => navigate('/astrologer/availability')}
                    >
                      Manage Availability
                    </Button>
                    <Button
                      variant="outlined"
                      startIcon={<Person />}
                      onClick={() => navigate('/astrologer/about')}
                    >
                      About Me
                    </Button>
                    <Button
                      variant="outlined"
                      startIcon={<AttachMoney />}
                      onClick={() => setActiveTab(2)}
                    >
                      View Earnings
                    </Button>
                    <Button
                      variant="outlined"
                      startIcon={<span style={{ fontSize: 20 }}>🪷</span>}
                      onClick={() => navigate('/vedic-tools')}
                      sx={{
                        borderColor: 'rgba(218,165,32,0.3)',
                        color: '#8B6914',
                        borderRadius: 2,
                        textTransform: 'none',
                        '&:hover': { borderColor: '#DAA520', background: 'rgba(218,165,32,0.05)' }
                      }}
                    >
                      Vedic Tools
                    </Button>
                    <Button
                      variant="outlined"
                      startIcon={<AccountBalance />}
                      onClick={() => navigate('/astrologer/earnings?tab=3')}
                    >
                      Payout Method
                    </Button>
                  </Box>
                </Paper>
              </Grid>
            </Grid>
          </>
        ) : activeTab === 1 ? (
          <Box>
            <Typography variant="h5" gutterBottom>
              Manage Your Availability
            </Typography>
            <Typography variant="body1" color="text.secondary" sx={{ mb: 3 }}>
              Set your availability using the Calendar v3 system
            </Typography>
            <Button
              variant="contained"
              size="large"
              onClick={() => navigate('/astrologer/availability')}
              startIcon={<CalendarToday />}
            >
              Go to Availability Calendar
            </Button>
          </Box>
        ) : (
          <Box>
            <Typography variant="h5" gutterBottom>
              Your Consultations
            </Typography>

            {/* Earnings Summary */}
            {earnings && (
              <Grid container spacing={2} sx={{ mb: 3 }}>
                <Grid item xs={12} sm={4}>
                  <Card>
                    <CardContent>
                      <Typography variant="body2" color="text.secondary">Today's Earnings</Typography>
                      <Typography variant="h5" color="success.main">₹{earnings.today_earnings || 0}</Typography>
                    </CardContent>
                  </Card>
                </Grid>
                <Grid item xs={12} sm={4}>
                  <Card>
                    <CardContent>
                      <Typography variant="body2" color="text.secondary">This Month</Typography>
                      <Typography variant="h5" color="primary.main">₹{earnings.month_earnings || 0}</Typography>
                    </CardContent>
                  </Card>
                </Grid>
                <Grid item xs={12} sm={4}>
                  <Card>
                    <CardContent>
                      <Typography variant="body2" color="text.secondary">Total Earnings</Typography>
                      <Typography variant="h5">₹{earnings.total || 0}</Typography>
                    </CardContent>
                  </Card>
                </Grid>
              </Grid>
            )}

            {/* All Upcoming Consultations */}
            <Card>
              <CardContent>
                <Typography variant="h6" gutterBottom>
                  All Upcoming Consultations ({upcomingTotal})
                </Typography>

                {loading.upcoming ? (
                  <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
                    <CircularProgress />
                  </Box>
                ) : upcomingConsultations.length === 0 ? (
                  <Paper sx={{ p: 4, textAlign: 'center' }}>
                    <Typography variant="h6" color="text.secondary" gutterBottom>
                      No bookings yet
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      When users book consultations with you, they will appear here.
                    </Typography>
                  </Paper>
                ) : (
                  <Box>
                    {upcomingConsultations.map((consultation) => (
                      <ConsultationItem
                        key={consultation.booking_id}
                        consultation={consultation}
                        onJoinChat={handleJoinChat}
                      />
                    ))}
                  </Box>
                )}
              </CardContent>
            </Card>

            <Box sx={{ mt: 2 }}>
              <Button
                variant="outlined"
                onClick={() => {
                  dispatch(fetchAstrologerUpcomingConsultations({ limit: 20 }));
                  dispatch(fetchAstrologerEarnings());
                }}
                disabled={loading.upcoming}
              >
                Refresh
              </Button>
            </Box>
          </Box>
        )}
      </Container>
    </>
  );
};

export default AstrologerDashboard;
